import { get } from 'svelte/store';

import { entryDraft } from '$lib/services/contents/draft';
import { getFieldValidationMessages } from '$lib/services/contents/draft/validate/messages';
import {
  getField,
  isFieldMultiple,
  isFieldRequired,
  LIST_KEY_PATH_REGEX,
} from '$lib/services/contents/entry/fields';
import { MEDIA_FIELD_TYPES, MIN_MAX_VALUE_FIELD_TYPES } from '$lib/services/contents/fields';
import { resolveCodeField } from '$lib/services/contents/fields/code/validate';
import { validateDateTimeField } from '$lib/services/contents/fields/date-time/validate';
import { validateKeyValueField } from '$lib/services/contents/fields/key-value/validate';
import { getListFieldInfo } from '$lib/services/contents/fields/list/helpers';
import { validateListField } from '$lib/services/contents/fields/list/validate';
import { validateNumberField } from '$lib/services/contents/fields/number/validate';
import { COMPONENT_NAME_PREFIX_REGEX } from '$lib/services/contents/fields/rich-text';
import { validateStringField } from '$lib/services/contents/fields/string/validate';
import { getRegex } from '$lib/services/utils/regex';

/**
 * @import {
 * DraftValueStoreKey,
 * EntryDraft,
 * EntryValidityState,
 * FlattenedEntryContent,
 * GetFieldArgs,
 * LocaleValidationMessagesMap,
 * LocaleValidityMap,
 * ValidateFieldFuncArgs,
 * } from '$lib/types/private';
 * @import {
 * CodeField,
 * Field,
 * FieldKeyPath,
 * ListField,
 * LocaleCode,
 * MinMaxValueField,
 * } from '$lib/types/public';
 */

/**
 * @typedef {object} ValidateFieldArgs
 * @property {EntryDraft} draft Entry draft.
 * @property {LocaleValidityMap} validities Validity state.
 * @property {LocaleCode} locale Current locale.
 * @property {FieldKeyPath} keyPath Field key path.
 * @property {FlattenedEntryContent} valueMap Entry values.
 * @property {any} value Field value.
 * @property {string} [componentName] Rich text editor component name.
 */

/**
 * @typedef {object} ValidationResults
 * @property {boolean} valid Whether the entry draft is valid.
 * @property {LocaleValidityMap} validities Validity state for each field in each locale.
 * @property {LocaleValidationMessagesMap} validationMessages Validation messages for each field in
 * each locale.
 */

/**
 * Default validity state for a field.
 * @type {EntryValidityState}
 * @internal
 */
export const DEFAULT_VALIDITY = {
  valueMissing: false,
  tooShort: false,
  tooLong: false,
  rangeUnderflow: false,
  rangeOverflow: false,
  patternMismatch: false,
  typeMismatch: false,
};

/**
 * Map of functions to validate different field types. Each function receives the field config and
 * the current value, and returns an object with the same properties as `EntryValidityState` except
 * `valid`.
 * @type {Record<string, (args: ValidateFieldFuncArgs) => { validity: EntryValidityState }>}
 * @internal
 */
export const VALIDATE_FIELD_FUNCTIONS = {
  datetime: validateDateTimeField,
  number: validateNumberField,
  string: validateStringField,
  text: validateStringField,
};

/**
 * Proxy handler for validity state. Exported for testing only.
 * @internal
 */
export const validityProxyHandler = {
  /**
   * Proxy getter.
   * @param {EntryValidityState} obj Object itself.
   * @param {string} prop Property name.
   * @returns {boolean | undefined} Property value.
   */
  get: (obj, prop) => (prop === 'valid' ? !Object.values(obj).some(Boolean) : obj[prop]),
};

/**
 * Validate a scalar field (all non-aggregate types), updating `validity` in place.
 * @param {object} args Arguments.
 * @param {any} args.value Current field value.
 * @param {boolean} args.required Whether the field is required.
 * @param {any} args.validation Pattern validation array or undefined.
 * @param {EntryValidityState} args.validity Validity state to update.
 */
const validateScalarField = ({ value, required, validation, validity }) => {
  const trimmed = typeof value === 'string' ? value.trim() : value;

  if (required && (trimmed === undefined || trimmed === null || trimmed === '')) {
    validity.valueMissing = true;
  }

  if (Array.isArray(validation)) {
    const regex = getRegex(validation[0]);

    if (regex && !regex.test(String(trimmed))) {
      validity.patternMismatch = true;
    }
  }
};

/**
 * Validate each field.
 * @internal
 * @param {ValidateFieldArgs} args Arguments.
 * @returns {EntryValidityState | undefined} Field validity.
 */
export const validateAnyField = (args) => {
  const { draft, locale, valueMap, componentName, validities } = args;
  const { collection, collectionName, fileName, collectionFile, files, isIndexFile } = draft;
  let { keyPath, value } = args;

  /** @type {GetFieldArgs} */
  const getFieldArgs = {
    collectionName,
    fileName,
    componentName,
    valueMap,
    keyPath: keyPath.replace(COMPONENT_NAME_PREFIX_REGEX, ''), // Remove component name prefix
    isIndexFile,
  };

  const fieldConfig = getField({ ...getFieldArgs });

  if (!fieldConfig) {
    return undefined;
  }

  // @ts-ignore Some field types don’t have `pattern` property
  const { widget: fieldType = 'string', i18n = false, pattern: validation } = fieldConfig;
  const multiple = isFieldMultiple(fieldConfig);

  const { min = 0, max = Infinity } = /** @type {MinMaxValueField} */ (
    MIN_MAX_VALUE_FIELD_TYPES.includes(fieldType) ? fieldConfig : {}
  );

  const { i18nEnabled, defaultLocale } = (collectionFile ?? collection)._i18n;

  // Skip validation on non-editable fields
  if (
    !componentName && // Don’t skip validation if the field is within a rich text editor component
    locale !== defaultLocale &&
    (!i18nEnabled || i18n === false || i18n === 'none' || i18n === 'duplicate')
  ) {
    return undefined;
  }

  const valueEntries = Object.entries(valueMap);
  const required = isFieldRequired({ fieldConfig, locale });
  /** @type {EntryValidityState} */
  const validity = { ...DEFAULT_VALIDITY };

  if (fieldType === 'list' || multiple) {
    const { skip } = validateListField({
      keyPath,
      value,
      valueEntries,
      validity,
      validities,
      locale,
      required,
      min,
      max,
    });

    if (skip) return undefined;
  }

  if (fieldType === 'object') {
    if (required && !value) {
      validity.valueMissing = true;
    }
  }

  if (fieldType === 'keyvalue') {
    const result = validateKeyValueField({
      keyPath,
      getFieldArgs,
      validity,
      validities,
      locale,
      required,
      min,
      max,
    });

    if (result.skip) return undefined;
    keyPath = result.keyPath;
  }

  if (fieldType === 'code') {
    const result = resolveCodeField({
      keyPath,
      value,
      valueMap,
      fieldConfig: /** @type {CodeField} */ (fieldConfig),
      validities,
      locale,
    });

    if (result.skip) return undefined;
    keyPath = result.keyPath;
    value = result.value;
  }

  if (
    MEDIA_FIELD_TYPES.includes(fieldType) &&
    typeof value === 'string' &&
    value.startsWith('blob:')
  ) {
    // The stored `value` is a blob URL; get the original file name
    value = files[value]?.file?.name;
  }

  if (!(['object', 'list', 'hidden', 'compute', 'keyvalue'].includes(fieldType) || multiple)) {
    validateScalarField({ value, required, validation, validity });
  }

  const validateFieldFn = VALIDATE_FIELD_FUNCTIONS[fieldType];

  if (validateFieldFn) {
    Object.assign(validity, validateFieldFn({ fieldConfig, locale, value }).validity);
  }

  return new Proxy(validity, validityProxyHandler);
};

/**
 * Validate a single field and update the validity state.
 * @internal
 * @param {ValidateFieldArgs} args Arguments.
 * @returns {boolean} Whether the field is valid.
 */
export const validateField = (args) => {
  const { validities, locale, keyPath } = args;
  const validity = validateAnyField(args);
  let valid = true;

  if (validity) {
    validities[locale][keyPath] = validity;

    if (!validity.valid) {
      valid = false;
    }
  }

  return valid;
};

/**
 * Validate an array-type field.
 * @internal
 * @param {object} args Arguments.
 * @param {Field} args.fieldConfig Field configuration.
 * @param {ValidateFieldArgs} args.validateArgs Arguments for field validation.
 * @returns {{ valid: boolean, validateItems: boolean }} Validation result.
 */
export const validateList = ({ fieldConfig, validateArgs }) => {
  const { validities, locale, keyPath } = validateArgs;
  const valid = validities[locale][keyPath]?.valid ?? validateField(validateArgs);
  const { widget: fieldType = 'string' } = fieldConfig;

  if (fieldType === 'list') {
    if (!getListFieldInfo(/** @type {ListField} */ (fieldConfig)).hasSubFields) {
      // Simple list field, so we don’t need to validate items
      return { valid, validateItems: false };
    }
  }

  if (isFieldMultiple(fieldConfig)) {
    // Same as a simple list field, so we don’t need to validate items
    return { valid, validateItems: false };
  }

  return { valid, validateItems: true };
};

/**
 * Validate the field values and return the results. Mimic the native `ValidityState` API.
 * @param {DraftValueStoreKey} valueStoreKey Key to store the values in {@link EntryDraft}.
 * @returns {ValidationResults} Validation results.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/ValidityState
 */
export const validateFields = (valueStoreKey) => {
  const draft = /** @type {EntryDraft} */ (get(entryDraft));
  const { collectionName, fileName, isIndexFile, currentLocales } = draft;
  /** @type {LocaleValidityMap} */
  const validities = {};
  /** @type {LocaleValidationMessagesMap} */
  const validationMessages = {};
  /** @type {GetFieldArgs} */
  const getFieldArgs = { collectionName, fileName, isIndexFile, keyPath: '', valueMap: {} };
  let valid = true;

  Object.entries(draft[valueStoreKey]).forEach(([locale, valueMap]) => {
    const valueEntries = Object.entries(valueMap);

    // If the locale is disabled, skip the validation and mark all fields valid
    if (!currentLocales[locale]) {
      validities[locale] = Object.fromEntries(
        valueEntries.map(([keyPath]) => [keyPath, { valid: true }]),
      );
      validationMessages[locale] = Object.fromEntries(
        valueEntries.map(([keyPath]) => [keyPath, []]),
      );

      return;
    }

    const validateArgs = { draft, locale, valueMap, validities };

    // Reset the state first
    validities[locale] = {};
    validationMessages[locale] = {};

    valueEntries.forEach(([keyPath, value]) => {
      const [prefix] = keyPath.match(COMPONENT_NAME_PREFIX_REGEX) ?? [];
      const componentName = prefix ? valueMap[`${prefix}__sc_component_name`] : undefined;

      const fieldConfig = getField({
        ...getFieldArgs,
        keyPath: keyPath.replace(COMPONENT_NAME_PREFIX_REGEX, ''), // Remove component name prefix
        valueMap,
        componentName,
      });

      if (!fieldConfig) {
        return;
      }

      // Validate a list itself before the items
      if (LIST_KEY_PATH_REGEX.test(keyPath)) {
        const listKeyPath = keyPath.replace(LIST_KEY_PATH_REGEX, '');

        const { valid: listValid, validateItems } = validateList({
          fieldConfig,
          validateArgs: {
            ...validateArgs,
            keyPath: listKeyPath,
            value: '',
            componentName,
          },
        });

        if (!listValid) {
          valid = false;
        }

        // Compute messages for the list field itself (only on first item iteration)
        if (!(listKeyPath in validationMessages[locale])) {
          const listValidity = validities[locale][listKeyPath];

          if (listValidity) {
            validationMessages[locale][listKeyPath] = getFieldValidationMessages({
              validity: listValidity,
              fieldConfig,
            });
          }
        }

        if (!validateItems) {
          return;
        }
      }

      if (!validateField({ ...validateArgs, keyPath, value, componentName })) {
        valid = false;
      }

      const validity = validities[locale][keyPath];

      if (validity) {
        validationMessages[locale][keyPath] = getFieldValidationMessages({ validity, fieldConfig });
      }
    });
  });

  return { valid, validities, validationMessages };
};
